import Image from "next/image"
import Link from "next/link"
import { ArrowDown, ArrowRight, BadgeCheck, ChevronRight, MousePointer2, Play, Sparkles, Star } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  HeroBackgroundVideoIsland,
  HeroCarouselIsland,
  HeroInlineFormIsland,
  HeroParallaxIsland,
  HeroSearchIsland,
  HeroTiltIsland,
  HeroTypewriterIsland,
  HeroVideoModalIsland,
} from "./hero-islands"
import type { HeroBackground, HeroCta, HeroEyebrow, HeroMedia, HeroShellProps, HeroSocialProof } from "./hero-types"

const defaultMedia: HeroMedia = {
  type: "dashboard",
  title: "Live preview",
}

function normalizeEyebrow(eyebrow?: string | HeroEyebrow): HeroEyebrow | undefined {
  if (!eyebrow) return undefined
  return typeof eyebrow === "string" ? { text: eyebrow, variant: "pill" } : eyebrow
}

function CtaCluster({ primaryCta, secondaryCta, tertiaryCta, trustMicrocopy, inverse = false }: { primaryCta?: HeroCta; secondaryCta?: HeroCta; tertiaryCta?: HeroCta; trustMicrocopy?: string; inverse?: boolean }) {
  if (!primaryCta && !secondaryCta && !tertiaryCta && !trustMicrocopy) return null
  return (
    <div className="mt-9 grid gap-4">
      {(primaryCta || secondaryCta || tertiaryCta) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {primaryCta && (
            <Button asChild size="xl" className={cn("group h-14 rounded-full px-7", inverse && "bg-background text-foreground hover:bg-background/90")}>
              <Link href={primaryCta.href}>{primaryCta.label}<ArrowRight className="transition-transform group-hover:translate-x-1" /></Link>
            </Button>
          )}
          {secondaryCta && (
            <Button asChild size="xl" variant="outline" className={cn("h-14 rounded-full px-7", inverse && "border-background/24 bg-background/10 text-background hover:bg-background/16 hover:text-background")}>
              <Link href={secondaryCta.href}>{secondaryCta.label}</Link>
            </Button>
          )}
          {tertiaryCta && (
            <Link href={tertiaryCta.href} className={cn("inline-flex h-12 items-center justify-center gap-2 rounded-full px-2 text-sm font-semibold text-primary underline-offset-4 hover:underline sm:h-auto", inverse && "text-background")}>{tertiaryCta.label}<ChevronRight className="size-4" /></Link>
          )}
        </div>
      )}
      {trustMicrocopy && <p className={cn("text-sm text-muted-foreground", inverse && "text-background/64")}>{trustMicrocopy}</p>}
    </div>
  )
}

function HeroHeading({ headline, highlight, enableTypewriter, inverse = false, layout }: Pick<HeroShellProps, "headline" | "highlight" | "enableTypewriter" | "layout"> & { inverse?: boolean }) {
  const hasHighlight = Boolean(highlight && headline.includes(highlight))
  const parts = hasHighlight && highlight ? headline.split(highlight) : [headline]
  const editorial = layout === "editorial"
  return (
    <h1 className={cn(
      "font-heading font-semibold tracking-tight",
      editorial ? "text-[clamp(4.25rem,12vw,10rem)] leading-[0.78]" : "text-5xl leading-[0.9] sm:text-6xl lg:text-7xl xl:text-8xl",
      inverse ? "text-background" : "text-foreground",
    )}>
      {hasHighlight && highlight ? <>{parts[0]}<span className="text-primary decoration-accent decoration-4 underline-offset-8 sm:underline">{highlight}</span>{parts.slice(1).join(highlight)}</> : headline}
      {enableTypewriter && <span className="block"><HeroTypewriterIsland words={[highlight ?? "faster", "clearer", "better"]} /></span>}
    </h1>
  )
}

function Eyebrow({ eyebrow, inverse = false }: { eyebrow?: HeroEyebrow; inverse?: boolean }) {
  if (!eyebrow) return null
  const content = (
    <span className={cn(
      "inline-flex items-center gap-2 text-sm font-semibold",
      eyebrow.variant === "pill" && "rounded-full border border-border/70 bg-background/78 px-4 py-2 shadow-sm backdrop-blur-xl",
      eyebrow.variant === "link" && "underline-offset-4 hover:underline",
      inverse && "border-background/16 bg-background/10 text-background",
      !inverse && eyebrow.variant !== "text" && "text-foreground",
      !inverse && eyebrow.variant === "text" && "text-primary",
    )}>
      {eyebrow.icon ?? <Sparkles className="size-4 text-primary" />}
      {eyebrow.text}
    </span>
  )
  return <div className="mb-6">{eyebrow.href ? <Link href={eyebrow.href}>{content}</Link> : content}</div>
}

function Announcement({ announcement, inverse = false }: Pick<HeroShellProps, "announcement"> & { inverse?: boolean }) {
  if (!announcement) return null
  const content = (
    <span className={cn("mb-5 inline-flex max-w-full min-w-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold shadow-sm backdrop-blur-xl", inverse ? "border-background/16 bg-background/10 text-background" : "border-border/70 bg-card text-foreground")}>
      {announcement.badge && <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">{announcement.badge}</span>}
      <span className="min-w-0 truncate">{announcement.text}</span>
      <ArrowRight className="size-4 shrink-0" />
    </span>
  )
  return announcement.href ? <Link href={announcement.href}>{content}</Link> : content
}

function BackgroundLayer({ background, atmosphere, inverse }: { background?: HeroBackground; atmosphere?: HeroShellProps["atmosphere"]; inverse: boolean }) {
  const type = background?.type ?? atmosphere ?? "gradient"
  const style = background?.color ? { backgroundColor: background.color } : background?.gradient ? { backgroundImage: background.gradient } : undefined
  return (
    <>
      <div className={cn(
        "absolute inset-0 -z-30",
        type === "solid" && "bg-background",
        type === "brand-block" && "bg-foreground",
        (type === "gradient" || type === "parallax") && "bg-[radial-gradient(circle_at_16%_18%,hsl(var(--primary)/0.18),transparent_30%),linear-gradient(135deg,hsl(var(--background)),hsl(var(--muted)/0.58))]",
        (type === "mesh" || type === "animated-mesh") && "bg-[radial-gradient(circle_at_18%_18%,hsl(var(--primary)/0.22),transparent_32%),radial-gradient(circle_at_82%_24%,hsl(var(--accent)/0.34),transparent_28%),linear-gradient(135deg,hsl(var(--background)),hsl(var(--muted)/0.64))] motion-safe:animate-gradient-shift",
        type === "pattern" && "bg-[linear-gradient(hsl(var(--border)/0.52)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--border)/0.52)_1px,transparent_1px)] bg-[size:44px_44px]",
        type === "particles" && "bg-[radial-gradient(circle_at_20%_20%,hsl(var(--primary)/0.2)_0_2px,transparent_3px),radial-gradient(circle_at_70%_35%,hsl(var(--accent)/0.26)_0_2px,transparent_3px),linear-gradient(135deg,hsl(var(--background)),hsl(var(--muted)/0.7))] bg-[size:120px_120px,180px_180px,auto]",
      )} style={style} />
      {background?.image?.src && <Image src={background.image.src} alt={background.image.alt} fill sizes="100vw" className="-z-20 object-cover" style={{ objectPosition: background.image.focalPoint ?? "center" }} priority />}
      {background?.video?.src && <HeroBackgroundVideoIsland src={background.video.src} poster={background.video.poster} captionTrackSrc={background.video.captionTrackSrc} />}
      {(background?.image || background?.video || type === "image" || type === "video" || inverse) && <div className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,hsl(var(--foreground)/0.82),hsl(var(--foreground)/0.34)_55%,hsl(var(--foreground)/0.12)),linear-gradient(180deg,hsl(var(--foreground)/0.28),hsl(var(--foreground)/0.66))]" />}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_48%_8%,hsl(var(--background)/0.12),transparent_34%)]" />
    </>
  )
}

function SocialProof({ socialProof, inverse = false }: { socialProof?: HeroSocialProof; inverse?: boolean }) {
  if (!socialProof) return null
  return (
    <div className="mt-10 grid gap-5">
      {(socialProof.avatars?.length || socialProof.rating || socialProof.count) && (
        <div className="flex flex-wrap items-center gap-4">
          {socialProof.avatars && (
            <div className="flex -space-x-3">
              {socialProof.avatars.slice(0, 5).map((avatar) => <span key={avatar.fallback} className="grid size-10 place-items-center rounded-full border-2 border-background bg-muted text-xs font-bold text-foreground shadow-sm">{avatar.src ? <Image src={avatar.src} alt={avatar.alt ?? "Customer avatar"} width={40} height={40} className="size-full rounded-full object-cover" /> : avatar.fallback}</span>)}
            </div>
          )}
          <div className={cn("text-sm", inverse ? "text-background/72" : "text-muted-foreground")}>
            {socialProof.rating && <p className="flex items-center gap-1 font-semibold"><Star className="size-4 fill-accent text-accent" />{socialProof.rating}</p>}
            {socialProof.count && <p>{socialProof.count}</p>}
          </div>
        </div>
      )}
      {socialProof.logos && <div className="flex flex-wrap gap-2">{socialProof.logos.slice(0, 6).map((logo) => <span key={logo.name} className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", inverse ? "border-background/16 bg-background/10 text-background/70" : "border-border bg-card text-muted-foreground")}>{logo.name}</span>)}</div>}
      {socialProof.quote && <blockquote className={cn("max-w-xl border-l-2 pl-4 text-sm leading-6", inverse ? "border-background/30 text-background/76" : "border-primary/40 text-muted-foreground")}>&ldquo;{socialProof.quote.body}&rdquo; <span className="font-semibold">{socialProof.quote.name}</span></blockquote>}
      {socialProof.stats && <dl className="grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">{socialProof.stats.map((stat) => <div key={stat.label} className={cn("rounded-card border p-4", inverse ? "border-background/14 bg-background/10" : "border-border/70 bg-card")}><dt className={cn("text-xs uppercase tracking-[0.14em]", inverse ? "text-background/56" : "text-muted-foreground")}>{stat.label}</dt><dd className={cn("mt-2 font-heading text-2xl font-semibold", inverse ? "text-background" : "text-foreground")}>{stat.value}</dd></div>)}</dl>}
    </div>
  )
}

function TerminalBlock({ media }: { media: HeroMedia }) {
  const lines = media.code ?? ["npx hiveku generate --brand luminous", "compose hero navigation proof pricing", "✓ page blueprint ready", "✓ schema hooks attached"]
  return (
    <div className="overflow-hidden rounded-card border border-background/12 bg-foreground text-background shadow-elegant">
      <div className="flex items-center gap-2 border-b border-background/10 bg-background/8 px-4 py-3"><span className="size-3 rounded-full bg-red-400" /><span className="size-3 rounded-full bg-yellow-300" /><span className="size-3 rounded-full bg-green-400" /><span className="ml-auto text-xs text-background/50">{media.title ?? "terminal"}</span></div>
      <pre className="min-h-[22rem] overflow-hidden p-5 text-sm leading-7 text-background/76"><code>{lines.join("\n")}</code></pre>
    </div>
  )
}

function BrowserFrame({ media, floating = false }: { media: HeroMedia; floating?: boolean }) {
  const imageSrc = media.type === "video" ? media.poster : media.src
  return (
    <div className={cn("overflow-hidden rounded-[1.75rem] border border-border/70 bg-card p-3 shadow-[0_34px_110px_hsl(var(--foreground)/0.18)]", floating && "rotate-[-2deg] lg:translate-y-8 lg:rotate-[-4deg]")}> 
      <div className="mb-3 flex items-center gap-2 rounded-card border border-border/70 bg-background px-4 py-3"><span className="size-3 rounded-full bg-red-400" /><span className="size-3 rounded-full bg-yellow-300" /><span className="size-3 rounded-full bg-green-400" /><span className="ml-auto h-2 w-28 rounded-full bg-muted" /></div>
      <div className="relative min-h-[24rem] overflow-hidden rounded-card bg-muted lg:min-h-[34rem]">
        {imageSrc ? <Image src={imageSrc} alt={media.alt ?? "Hero media"} fill sizes="(min-width: 1024px) 48vw, 100vw" className="object-cover" style={{ objectPosition: media.focalPoint ?? "center" }} priority /> : <DashboardPlaceholder />}
        {media.type === "video" && <HeroVideoModalIsland poster={media.poster} src={media.src} />}
      </div>
    </div>
  )
}

function DashboardPlaceholder() {
  return <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--muted))),radial-gradient(circle_at_80%_18%,hsl(var(--primary)/0.18),transparent_30%)] p-5"><div className="grid h-full grid-cols-[0.7fr_1.3fr] gap-4"><div className="rounded-card bg-background/80" /><div className="grid gap-4"><div className="rounded-card bg-background/80" /><div className="grid grid-cols-2 gap-4"><div className="rounded-card bg-primary/18" /><div className="rounded-card bg-background/80" /></div></div></div></div>
}

function DeviceMockup({ media }: { media: HeroMedia }) {
  const frame = <div className="mx-auto max-w-sm rounded-panel border border-border/70 bg-foreground p-3 shadow-elegant"><div className="relative min-h-[36rem] overflow-hidden rounded-panel bg-background">{media.src ? <Image src={media.src} alt={media.alt ?? "Device preview"} fill sizes="22rem" className="object-cover" priority /> : <DashboardPlaceholder />}</div></div>
  return <HeroTiltIsland>{frame}</HeroTiltIsland>
}

function MediaSlot({ media = defaultMedia, layout, carouselSlides, enableTilt }: Pick<HeroShellProps, "media" | "layout" | "carouselSlides" | "enableTilt">) {
  if (carouselSlides?.length) return <HeroCarouselIsland slides={carouselSlides} />
  if (media.type === "terminal" || layout === "terminal") return <TerminalBlock media={media} />
  if (media.type === "device" || layout === "device") return <DeviceMockup media={media} />
  const frame = <BrowserFrame media={media} floating={media.type === "screenshot"} />
  return enableTilt ? <HeroTiltIsland>{frame}</HeroTiltIsland> : frame
}

function DualAudience({ items, inverse = false }: { items?: HeroShellProps["dualAudience"]; inverse?: boolean }) {
  if (!items?.length) return null
  return <div className="mt-10 grid gap-4 md:grid-cols-2">{items.slice(0, 2).map((item) => <Link key={item.title} href={item.cta.href} className={cn("group rounded-card border p-5 transition-transform hover:-translate-y-1", inverse ? "border-background/14 bg-background/10 text-background" : "border-border bg-card text-foreground")}><p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{item.label}</p><h2 className="mt-4 font-heading text-2xl font-semibold tracking-tight">{item.title}</h2><p className={cn("mt-3 text-sm leading-6", inverse ? "text-background/64" : "text-muted-foreground")}>{item.description}</p><span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">{item.cta.label}<ArrowRight className="size-4 transition-transform group-hover:translate-x-1" /></span></Link>)}</div>
}

export function HeroShell({
  eyebrow,
  announcement,
  headline,
  highlight,
  subheadline,
  primaryCta,
  secondaryCta,
  tertiaryCta,
  trustMicrocopy,
  media,
  background,
  socialProof,
  form,
  layout = "split-left",
  atmosphere = "gradient",
  alignment,
  density = "standard",
  scrollCue = false,
  enableParallax = false,
  enableTilt = false,
  enableTypewriter = false,
  carouselSlides,
  dualAudience,
  className,
}: HeroShellProps) {
  const normalizedEyebrow = normalizeEyebrow(eyebrow)
  const inverse = atmosphere === "brand-block" || background?.type === "image" || background?.type === "video" || layout === "full-bleed"
  const centered = layout === "centered" || alignment === "center" || layout === "minimal" || layout === "editorial"
  const copyFirst = layout !== "split-right"
  const parallaxId = "hero-parallax-layer"
  const padding = density === "compact" ? "py-16 sm:py-20" : density === "spacious" ? "py-24 sm:py-32 lg:py-40" : "py-20 sm:py-24 lg:py-28"

  const copy = (
    <div className={cn("relative z-10 min-w-0 max-w-4xl", centered && "mx-auto text-center", !centered && "max-w-3xl")}>
      <Announcement announcement={announcement} inverse={inverse} />
      <Eyebrow eyebrow={normalizedEyebrow} inverse={inverse} />
      <HeroHeading headline={headline} highlight={highlight} enableTypewriter={enableTypewriter} inverse={inverse} layout={layout} />
      {subheadline && <p className={cn("mt-7 max-w-2xl text-lg leading-8 sm:text-xl", centered && "mx-auto", inverse ? "text-background/72" : "text-muted-foreground")}>{subheadline}</p>}
      <CtaCluster primaryCta={primaryCta} secondaryCta={secondaryCta} tertiaryCta={tertiaryCta} trustMicrocopy={trustMicrocopy} inverse={inverse} />
      {form && <div className="mt-8"><HeroInlineFormIsland form={form} compact={form.fields.length === 1} /></div>}
      {layout === "dual-audience" && <DualAudience items={dualAudience} inverse={inverse} />}
      <SocialProof socialProof={socialProof} inverse={inverse} />
    </div>
  )

  const mediaSlot = layout !== "minimal" && layout !== "dual-audience" ? <div id={enableParallax ? parallaxId : undefined} className="relative z-10 min-w-0"><MediaSlot media={media} layout={layout} carouselSlides={carouselSlides} enableTilt={enableTilt} /></div> : null

  return (
    <section className={cn("relative isolate w-full max-w-full overflow-hidden", inverse ? "bg-foreground text-background" : "bg-background text-foreground", padding, className)}>
      <BackgroundLayer background={background} atmosphere={atmosphere} inverse={inverse} />
      {enableParallax && <HeroParallaxIsland targetId={parallaxId} />}
      <div aria-hidden="true" className="absolute left-1/2 top-10 -z-10 h-64 w-[min(42rem,86vw)] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
      <div className={cn("mx-auto grid w-full min-w-0 max-w-7xl gap-12 px-4 sm:px-6 lg:px-8", centered ? "items-center" : "items-center lg:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)]", layout === "asymmetric" && "lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]")}> 
        {centered ? (
          <>
            {copy}
            {(mediaSlot || layout === "inline-form") && <div className="mx-auto w-full max-w-5xl">{mediaSlot}</div>}
          </>
        ) : copyFirst ? (
          <>{copy}{mediaSlot}</>
        ) : (
          <>{mediaSlot}{copy}</>
        )}
      </div>
      {layout === "inline-form" && !form && <div className="mx-auto mt-8 max-w-2xl px-4 sm:px-6 lg:px-8"><HeroSearchIsland placeholder="Search services, resources, or products" /></div>}
      {scrollCue && <a href="#main-content" aria-label="Scroll to content" className={cn("absolute bottom-5 left-1/2 hidden -translate-x-1/2 rounded-full border p-3 shadow-sm md:grid", inverse ? "border-background/20 bg-background/10 text-background" : "border-border bg-background text-foreground")}><ArrowDown className="size-5 motion-safe:animate-bounce" /></a>}
      <div aria-hidden="true" className="pointer-events-none absolute bottom-6 right-6 hidden items-center gap-2 text-xs text-muted-foreground lg:flex"><MousePointer2 className="size-3" /> first impression</div>
    </section>
  )
}
